/**
 * GalleryEditorPanel — dedicated Gallery editor. Exposes exactly the controls the production
 * Gallery component (`site-builder/components/gallery/Gallery.tsx`) actually renders: a
 * centered heading + description (same shared `InfoBlock` as Services/About/Banner — real
 * despite the registry declaring `subheading`/`actions` unsupported for `gallery`) and the
 * image list. No Eyebrow — the shared `InfoBlock` never renders eyebrow text.
 *
 * No Layout control — the reference gallery has exactly one visual presentation; `section.layout`
 * still exists on the schema (frozen contract, untouched) and the registry still lists
 * `grid`/`masonry`, but neither is exposed here.
 *
 * Gallery UX improvement:
 *  - Multi-file upload: the add tile accepts selecting several images at once; they're read and
 *    appended in the order the user picked them (never reordered, never prepended).
 *  - Capped at `MAX_GALLERY_IMAGES` (10) images; once reached, the add tile disables and a
 *    "Maximum 10 images reached" message replaces it.
 *  - Gallery uploads validate against `readGalleryImageFile`/`MAX_GALLERY_IMAGE_BYTES` (2MB) — a
 *    dedicated, larger cap than the shared `readImageFile`/`MAX_IMAGE_BYTES` used by every other
 *    section's `ImageUploadField`, which is left untouched.
 *  - Sidebar thumbnails are redesigned: larger, equal-size, more spacing, a numbered index badge,
 *    and the existing left/right reorder controls (the closest thing to a "drag handle" this
 *    editor has) restyled to sit clearly below each tile instead of cramped inline.
 *
 * Add/Remove/Replace all go through the asset library (see `applyAddGalleryImage` and friends in
 * BuilderEditorContext): every image is a real Asset id, removal prunes the asset unless still
 * referenced elsewhere, and replace mutates the existing asset's URL in place — so there is never
 * a duplicate or orphaned asset reference. None of that data-layer logic changed here.
 */
import { useRef, useState } from 'react'
import { ChevronLeft, ChevronRight, Plus, X } from 'lucide-react'
import { useBuilderEditor, assetUrl, readGalleryImageFile, MAX_GALLERY_IMAGES } from './editorContext'
import { Fieldset, TextField, TextArea, ToggleField } from './controls'

export function GalleryEditorPanel({ sectionId }: { sectionId: string }) {
  const e = useBuilderEditor()
  const [error, setError] = useState<string | null>(null)
  const section = e.schema.pages[0]?.sections.find((s) => s.id === sectionId)
  if (!section) {
    return <p className="px-5 py-6 text-xs text-gray-400">This section is no longer available.</p>
  }

  const media = section.content.media ?? []
  const atMax = media.length >= MAX_GALLERY_IMAGES

  const onReplace = (file: File | undefined, mediaId: string) => {
    if (!file) return
    setError(null)
    readGalleryImageFile(file)
      .then((src) => e.replaceGalleryImage(sectionId, mediaId, src))
      .catch((err: Error) => setError(err.message))
  }

  /** Reads every selected file in order and appends each as a new image — always at the end. */
  const onAdd = async (files: FileList | null) => {
    if (!files || files.length === 0) return
    setError(null)
    const remaining = MAX_GALLERY_IMAGES - media.length
    const selected = Array.from(files).slice(0, remaining)
    if (files.length > remaining) {
      setError(`Maximum ${MAX_GALLERY_IMAGES} images reached.`)
    }
    for (const file of selected) {
      try {
        const src = await readGalleryImageFile(file)
        e.addGalleryImage(sectionId, src)
      } catch (err) {
        setError((err as Error).message)
        break
      }
    }
  }

  return (
    <div>
      <Fieldset title="Visibility">
        <ToggleField
          label="Show Gallery"
          description="Hide to remove it from the published site"
          checked={section.visible}
          onChange={() => e.toggleSectionVisible(sectionId)}
        />
      </Fieldset>

      <Fieldset title="Content">
        <TextField label="Heading" value={section.content.heading ?? ''} onChange={(v) => e.setSectionText(sectionId, 'heading', v)} />
        <TextArea label="Description" value={section.content.subheading ?? ''} rows={3} onChange={(v) => e.setSectionText(sectionId, 'subheading', v)} />
      </Fieldset>

      <Fieldset title="Images">
        <div className="grid grid-cols-3 gap-3">
          {media.map((m, i) => (
            <GalleryThumbnail
              key={m.id}
              index={i + 1}
              src={assetUrl(e.schema, m.assetId) ?? ''}
              canMoveLeft={i > 0}
              canMoveRight={i < media.length - 1}
              onReplace={(file) => onReplace(file, m.id)}
              onRemove={() => e.removeGalleryImage(sectionId, m.id)}
              onMoveLeft={() => e.moveGalleryImage(sectionId, m.id, 'up')}
              onMoveRight={() => e.moveGalleryImage(sectionId, m.id, 'down')}
            />
          ))}
          {atMax ? null : <AddTile onPick={onAdd} />}
        </div>
        {atMax ? (
          <p className="mt-2 text-[11px] text-gray-500">Maximum {MAX_GALLERY_IMAGES} images reached.</p>
        ) : (
          <p className="mt-2 text-[11px] text-gray-400">
            Up to {MAX_GALLERY_IMAGES} images, 2MB each. Select multiple files at once to add them all in order.
          </p>
        )}
        {error ? <span className="mt-1 block text-[11px] text-red-600">{error}</span> : null}
      </Fieldset>
    </div>
  )
}

const TILE = 'aspect-square w-full rounded-2xl border border-gray-200'

/** One gallery thumbnail: click the image to replace it, badge to remove, arrows to reorder. */
function GalleryThumbnail({
  index,
  src,
  canMoveLeft,
  canMoveRight,
  onReplace,
  onRemove,
  onMoveLeft,
  onMoveRight,
}: {
  index: number
  src: string
  canMoveLeft: boolean
  canMoveRight: boolean
  onReplace: (file: File | undefined) => void
  onRemove: () => void
  onMoveLeft: () => void
  onMoveRight: () => void
}) {
  const inputRef = useRef<HTMLInputElement>(null)

  return (
    <div className="flex flex-col items-center gap-1.5">
      <div className={`relative ${TILE} overflow-hidden bg-white shadow-sm`}>
        <button
          type="button"
          onClick={() => inputRef.current?.click()}
          className="h-full w-full"
          aria-label="Replace image"
          title="Replace image"
        >
          <img src={src} alt="" className="h-full w-full object-cover" />
        </button>
        <span className="pointer-events-none absolute bottom-1.5 left-1.5 rounded-full bg-black/60 px-1.5 py-0.5 text-[10px] font-medium text-white">
          {index}
        </span>
        <button
          type="button"
          onClick={onRemove}
          aria-label="Remove image"
          title="Remove image"
          className="absolute -right-1.5 -top-1.5 flex h-5 w-5 items-center justify-center rounded-full border border-gray-200 bg-white text-gray-500 shadow-sm transition-colors hover:text-red-600"
        >
          <X className="h-3 w-3" />
        </button>
        <input
          ref={inputRef}
          type="file"
          accept="image/*"
          className="hidden"
          onChange={(ev) => { onReplace(ev.target.files?.[0]); ev.target.value = '' }}
        />
      </div>
      <div className="flex items-center gap-1 rounded-full bg-gray-50 px-1 py-0.5">
        <button
          type="button"
          onClick={onMoveLeft}
          disabled={!canMoveLeft}
          aria-label="Move image left"
          title="Move left"
          className="rounded-full p-1 text-gray-400 transition-colors hover:bg-gray-100 hover:text-gray-700 disabled:opacity-30 disabled:hover:bg-transparent"
        >
          <ChevronLeft className="h-3.5 w-3.5" />
        </button>
        <button
          type="button"
          onClick={onMoveRight}
          disabled={!canMoveRight}
          aria-label="Move image right"
          title="Move right"
          className="rounded-full p-1 text-gray-400 transition-colors hover:bg-gray-100 hover:text-gray-700 disabled:opacity-30 disabled:hover:bg-transparent"
        >
          <ChevronRight className="h-3.5 w-3.5" />
        </button>
      </div>
    </div>
  )
}

/** Trailing tile — same size as a thumbnail, "+", opens the file picker to append new images. */
function AddTile({ onPick }: { onPick: (files: FileList | null) => void }) {
  const inputRef = useRef<HTMLInputElement>(null)
  return (
    <div className="flex flex-col items-center gap-1.5">
      <button
        type="button"
        onClick={() => inputRef.current?.click()}
        aria-label="Add images"
        title="Add images"
        className={`${TILE} flex items-center justify-center border-dashed bg-white text-gray-400 transition-colors hover:bg-gray-50 hover:text-gray-600`}
      >
        <Plus className="h-6 w-6" />
      </button>
      {/* Height-only spacer matching the reorder-arrow row under each thumbnail, so the add
          tile's image box lines up with the others. */}
      <div className="h-6" aria-hidden="true" />
      <input
        ref={inputRef}
        type="file"
        accept="image/*"
        multiple
        className="hidden"
        onChange={(ev) => { onPick(ev.target.files); ev.target.value = '' }}
      />
    </div>
  )
}
